"use client"

import {connect} from "react-redux";
import { useState, useEffect, useCallback } from "react"
import { motion } from "framer-motion"
import * as Icons from "lucide-react"
import { getWorkflowStatistics, getWorkflowStatisticsp2p } from "../../../../ApiCalls/mantisFlowsManangment"
import { selectAuthState } from "../../../../store/selectors/authSelectors";

// Caché global para estadísticas (persiste entre renderizados)
const statsCache = new Map();

// Tiempo de expiración del caché en milisegundos (5 minutos)
const CACHE_EXPIRY_TIME = 5 * 60 * 1000;

function FlowStats({ flowData, token, mantisBusiness }) {
  const [stats, setStats] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [cacheHit, setCacheHit] = useState(false)

  // Función para obtener el icono dinámicamente
  const getIcon = (iconName) => {
    const iconNamePascal = iconName.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join('')
    
    const Icon = Icons[iconNamePascal] || Icons.BarChart4
    return <Icon className="h-5 w-5" />
  }

  // Función para obtener el color del icono basado en el nombre
  const getIconColor = (color, name) => {
    // Si hay un color definido en la BD, usarlo
    if (color) {
      switch (color.toLowerCase()) {
        case 'blue': return 'text-blue-500';
        case 'green': return 'text-emerald-500';
        case 'red': return 'text-red-500';
        case 'yellow': return 'text-amber-500';
        case 'purple': return 'text-purple-500';
        case 'orange': return 'text-orange-500';
        case 'teal': return 'text-teal-500';
        case 'indigo': return 'text-indigo-500';
        case 'pink': return 'text-pink-500';
        default: return 'text-gray-500';
      }
    }
    
    // Fallback al comportamiento anterior basado en el nombre
    const nameLower = name.toLowerCase()
    if (nameLower.includes('ganancia')) return 'text-emerald-500'
    if (nameLower.includes('ingreso')) return 'text-blue-500'
    if (nameLower.includes('actualización')) return 'text-purple-500'
    if (nameLower.includes('repeticiones')) return 'text-amber-500'
    if (nameLower.includes('categorías únicas operacionales')) return 'text-amber-500'
    return 'text-gray-500'
  }

  // Función para formatear valores, incluyendo fechas
  const formatStatValue = (value) => {
    // Verificar si es una fecha válida
    if (typeof value === 'string') {
      // Patrones comunes de fechas
      const datePatterns = [
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO format
        /^\w{3} \w{3} \d{2} \d{4} \d{2}:\d{2}:\d{2}/, // Mon Apr 14 2025 format
        /^\d{4}-\d{2}-\d{2}/ // YYYY-MM-DD format
      ];
      
      // Verificar si el string coincide con algún patrón de fecha
      const isDate = datePatterns.some(pattern => pattern.test(value));
      
      if (isDate) {
        try {
          const date = new Date(value);
          // Verificar que la fecha sea válida
          if (!isNaN(date.getTime())) {
            // Formatear la fecha en un formato más amigable
            return date.toLocaleDateString('es-CO', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            });
          }
        } catch (e) {
          // Si hay error al parsear, devolver el valor original
          return value;
        }
      }
    }
    
    // Si es un número, formatear con separadores de miles
    if (typeof value === 'number' || (typeof value === 'string' && !isNaN(value))) {
      try {
        const num = Number(value);
        return num.toLocaleString('es-CO');
      } catch (e) {
        return value;
      }
    }
    
    // Para otros tipos, devolver el valor original
    return value;
  };

  // Extraer la función loadStats para poder usarla con useCallback
  const loadStats = useCallback(async (forceRefresh = false) => {
    try {
      const workflowId = flowData?.id
      if (!workflowId) {
        throw new Error("ID del flujo no disponible")
      }
      
      const clientId = mantisBusiness._id;
      
      // Clave única para el caché
      const cacheKey = `${clientId}_${workflowId}`;
      
      // Verificar si hay datos en caché y no están expirados
      const cachedData = statsCache.get(cacheKey);
      const now = Date.now();
      
      if (!forceRefresh && cachedData && (now - cachedData.timestamp < CACHE_EXPIRY_TIME)) {
        console.log(`[FlowStats] Usando estadísticas en caché para flujo ${workflowId}`);
        setStats(cachedData.data);
        setIsLoading(false);
        setCacheHit(true);
        return;
      }
      
      // Si no hay caché o se fuerza la recarga, hacer la petición
      setIsLoading(true)
      setError(null)
      setCacheHit(false)
      
      console.log(`[FlowStats] Cargando estadísticas frescas para flujo ${workflowId}`);
      let response;
      const user_id = mantisBusiness;
      if ( user_id === "67f3f66b52bb5c93527c1542" || user_id === "682b910ca728c4cd1996e3d2" || user_id === "6824470343029ff9bb1b72d4") {
        response = await getWorkflowStatisticsp2p(clientId, workflowId);
      } else {
        response = await getWorkflowStatistics(clientId, workflowId);
      }
      
      if (response.success && response.data?.[0]?.data) {
        const formattedStats = response.data[0].data.map(stat => ({
          id: stat.id,
          name: stat.name,
          icon: stat.icon || 'bar-chart-2',
          color: stat.color || 'gray', // Usar el color de la BD
          value: formatStatValue(stat.value) || 'No disponible'
        }))
        
        // Guardar en caché con timestamp
        statsCache.set(cacheKey, {
          data: formattedStats,
          timestamp: now
        });
        
        setStats(formattedStats)
      } else {
        const defaultStat = [{
          id: 0,
          name: "Estadísticas",
          icon: "alert-circle",
          color: "gray",
          value: "No disponibles"
        }];
        
        // También guardamos este resultado en caché
        statsCache.set(cacheKey, {
          data: defaultStat,
          timestamp: now
        });
        
        setStats(defaultStat)
      }
    } catch (err) {
      console.error("Error al cargar estadísticas:", err)
      setError(err.message)
      setStats([{
        id: 0,
        name: "Error",
        icon: "x-circle",
        color: "red",
        value: "Error al cargar estadísticas"
      }])
    } finally {
      setIsLoading(false)
    }
  }, [flowData?.id, mantisBusiness._id]);

  // Efecto para cargar estadísticas solo cuando cambia el ID del flujo
  useEffect(() => {
    if (flowData?.id) {
      loadStats(false); // false = usar caché si está disponible
    }
  }, [flowData?.id, loadStats]); // Solo depende del ID del flujo, no de todo flowData

  // Efecto para recargar cuando se activa refreshTrigger
  useEffect(() => {
    if (refreshTrigger > 0) {
      loadStats(true); // true = forzar recarga ignorando caché
    }
  }, [refreshTrigger, loadStats]);

  // Añadir este efecto para escuchar el evento de actualización de estadísticas
  useEffect(() => {
    // Función para manejar el evento de actualización de estadísticas
    const handleStatsRefresh = (event) => {
      const eventFlowId = event.detail?.flowId;
      
      // Solo actualizar si el evento es para este flujo
      if (eventFlowId && eventFlowId === flowData?.id) {
        console.log(`[FlowStats] Recibido evento de actualización para flujo ${eventFlowId}`);
        loadStats(true); // Forzar recarga ignorando caché
      }
    };
    
    // Registrar el listener para el evento
    window.addEventListener('refreshFlowStats', handleStatsRefresh);
    
    // Limpiar el listener cuando el componente se desmonte
    return () => {
      window.removeEventListener('refreshFlowStats', handleStatsRefresh);
    };
  }, [flowData?.id, loadStats]);

  // Función para forzar la recarga de estadísticas
  const refreshStats = () => {
    setRefreshTrigger(prev => prev + 1)
  }

  const StatItem = ({ name, value, icon, color }) => (
    <div className={`
      relative flex items-center gap-3 px-4 py-3
      ${isLoading ? 'animate-pulse' : ''}
      transition-all duration-300
    `}>
      <div className={`
        flex items-center justify-center w-8 h-8 rounded-full
        bg-gradient-to-br from-slate-50 to-slate-100
        dark:from-slate-800 dark:to-slate-900
        shadow-sm border border-slate-200 dark:border-slate-700
      `}>
        <span className={getIconColor(color, name)}>
          {getIcon(icon)}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
          {name}
        </p>
        <p className="text-lg font-bold text-slate-700 dark:text-slate-300">
          {value}
        </p>
      </div>
    </div>
  )

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mt-6"
    >
      <div className="bg-white dark:bg-slate-800 rounded shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="flex justify-between items-center px-4 py-2 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Estadísticas del flujo
          </h3>
          <button 
            onClick={refreshStats}
            className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 flex items-center gap-1"
            title="Actualizar estadísticas"
          >
            <Icons.RefreshCw className="h-3 w-3" />
            <span>{cacheHit ? "Actualizar" : "Recargar"}</span>
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-slate-200 dark:divide-slate-700">
          {stats.map((stat) => (
            <StatItem 
              key={stat.id}
              name={stat.name}
              value={stat.value}
              icon={stat.icon}
              color={stat.color}
            />
          ))}
        </div>
      </div>
    </motion.div>
  )
}

export default connect(selectAuthState)(FlowStats);